import { Ad } from '@column/lib/types/ad';
import { Product } from '@column/lib/enums';
import { MadlibDataType } from '@column/lib/types/madlib';
import MadlibEditor from 'routes/madlib/components/MadlibEditor';
import { LaunchDarklyFlags } from '@column/lib/types/launchDarklyFlags';
import { useBooleanFlag } from 'utils/flags';
import { Layout } from '@column/lib/types/layout';
import MinimalMce, { MceToolbarOptions } from '../MinimalMce';
import { AdEditorData } from '../FormattedEditorWrapper';
import CopilotEditor from '../CopilotEditor';
import { ORDER_PLACEMENT_COLORS } from '../ColorSwatchPanel';
import MadlibInfo from '../MadlibInfo';

const contentPlaceholderByProduct: Record<Product, string> = {
  [Product.Classified]: 'Ad content',
  [Product.Notice]: 'Notice content',
  [Product.Obituary]: 'Obituary text...'
};

export type MadlibEditorProps = {
  madlibData: MadlibDataType;
  madlibTemplate: string | undefined;
  setValidationResults: (results: Record<string, boolean>) => void;
  setRenderedHtml: (html: string) => void;
  setMadlibData: (data: MadlibDataType) => void;
};

type ContentEditorProps<T extends Ad> = {
  adData: Partial<T>;
  onChange: (data: AdEditorData) => void;
  disableEditing: boolean;
  product: Product;
  madlibProps: MadlibEditorProps;
  forceFreeEdit: boolean;
  maxWords?: number | null;
  layout: Layout;
};

function ContentEditor<T extends Ad>({
  adData,
  onChange,
  disableEditing,
  product,
  madlibProps,
  forceFreeEdit,
  maxWords,
  layout
}: ContentEditorProps<T>) {
  const {
    madlibData,
    madlibTemplate,
    setValidationResults,
    setRenderedHtml,
    setMadlibData
  } = madlibProps;

  const showMadlibEditor = !!madlibTemplate;

  const ContentEditorComponent = useBooleanFlag(
    LaunchDarklyFlags.ENABLE_COPILOT_EDITOR
  )
    ? CopilotEditor
    : MinimalMce;

  if (showMadlibEditor && !forceFreeEdit) {
    return (
      <>
        <MadlibInfo
          isOverrideActive={forceFreeEdit}
          product={product}
          onDisableMadlib={() => {
            setMadlibData({
              templateData: {},
              questionTemplateData: {},
              editorOverride: true
            });
          }}
        />
        <MadlibEditor
          rawTemplate={madlibTemplate}
          setRenderedHtml={setRenderedHtml}
          onTemplateDataChange={setMadlibData}
          madlibData={madlibData}
          setValidationResults={setValidationResults}
          /**
           * We remove the `madlibData` and `content` fields from the handlebar data because we refresh the editor anytime the handlebar data changes.
           * Leaving these fields defined would cause the editor to refresh any time the user typed in the editor.
           * */
          handlebarData={{
            ...adData,
            madlibData: undefined,
            content: undefined
          }}
          ignoreStyling
        />
      </>
    );
  }

  const toolbarItems: MceToolbarOptions[] = [
    'undo',
    'redo',
    'bold',
    'italic',
    'forecolor'
  ];

  const colorOptionNamesByHexCode = Object.fromEntries(
    ORDER_PLACEMENT_COLORS.map(color => [color, color])
  );

  return (
    <ContentEditorComponent
      toolbarItems={[toolbarItems]}
      onChange={content => onChange({ content })}
      inline
      minHeight={150}
      value={adData?.content || ''}
      id="ad-content-editor"
      disabled={disableEditing}
      placeholder={
        layout.bodyConfig?.placeholder || contentPlaceholderByProduct[product]
      }
      required={!layout.bodyConfig?.optional}
      maxWords={maxWords}
      adData={adData}
      colorOptionNamesByHexCode={colorOptionNamesByHexCode}
      forbidCustomColors
    />
  );
}

export default ContentEditor;
